import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TrainingAuthProvider } from './context/TrainingAuthContext';
import { ClientAuthProvider } from './context/ClientAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TrainingProtectedRoute from './components/TrainingProtectedRoute';
import ClientProtectedRoute from './components/ClientProtectedRoute';
import Home from './pages/Home';
import InspectionModule from './pages/InspectionModule';
import TrainingModule from './pages/TrainingModule';
import Register from './pages/Register';
import Login from './pages/Login';
import TrainingLogin from './pages/TrainingLogin';
import TrainingRegister from './pages/TrainingRegister';
import JuniorInspectorDashboard from './pages/dashboards/JuniorInspectorDashboard';
import SeniorInspectorDashboard from './pages/dashboards/SeniorInspectorDashboard';
import AdminDashboard from './pages/dashboards/AdminDashboard';
import ManageEmployeesClients from './pages/admin/ManageEmployeesClients';
import NewInspectionForm from './pages/NewInspectionForm';
import DynamicForm from './pages/DynamicForm';
import ViewSubmissionDetails from './pages/ViewSubmissionDetails';
import EditSubmission from './pages/EditSubmission';
import EditProfile from './pages/EditProfile';
import ChangePassword from './pages/ChangePassword';
import Reports from './pages/Reports';
import InspectionReports from './pages/InspectionReports';
import TrainingReports from './pages/TrainingReports';
import MyDrafts from './pages/MyDrafts';
import CertificateVerification from './pages/CertificateVerification';
import TrainingDashboard from './pages/training/TrainingDashboard';
import TrainingCoordinatorDashboard from './pages/training/TrainingCoordinatorDashboard';
import TrainingReviewerDashboard from './pages/training/TrainingReviewerDashboard';
import NewTrainingCertificate from './pages/training/NewTrainingCertificate';
import ViewTrainingSubmission from './pages/training/ViewTrainingSubmission';
import EditTrainingSubmission from './pages/training/EditTrainingSubmission';
import TrainingEditProfile from './pages/training/TrainingEditProfile';
import TrainingChangePassword from './pages/training/TrainingChangePassword';
import TrainingReportsPage from './pages/training/TrainingReportsPage';
import TrainingCertVerification from './pages/training/TrainingCertVerification';
import ClientLogin from './pages/client/ClientLogin';
import ClientRegister from './pages/client/ClientRegister';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientProfile from './pages/client/ClientProfile';
import ClientChangePassword from './pages/client/ClientChangePassword';
import './App.css';

// Shown when another tab has logged in as a different user
const SessionConflictOverlay = () => {
  const { sessionConflict, dismissConflict } = useAuth();
  const navigate = useNavigate();

  if (!sessionConflict) return null;

  const handleGoToLogin = () => {
    dismissConflict();
    navigate('/login');
  };

  const roleColors = {
    'Admin': '#dc3545',
    'Reviewer': '#fd7e14',
    'Inspector': '#0d6efd'
  };
  const color = roleColors[sessionConflict.newUserRole] || '#6c757d';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '2.5rem',
        maxWidth: '440px', width: '90%', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
      }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>⚠️</div>
        <h4 style={{ color: '#dc3545', fontWeight: 700, marginBottom: '0.75rem' }}>
          Session Conflict Detected
        </h4>
        <p style={{ color: '#495057', marginBottom: '0.5rem' }}>
          Another user has signed in on this browser:
        </p>
        <div style={{
          backgroundColor: '#f8f9fa', border: '1px solid #dee2e6',
          borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem'
        }}>
          <strong style={{ fontSize: '1.1rem' }}>{sessionConflict.newUserName}</strong>
          <br />
          <span style={{
            display: 'inline-block', marginTop: '0.25rem',
            backgroundColor: color, color: '#fff',
            borderRadius: '999px', padding: '2px 12px', fontSize: '0.8rem'
          }}>
            {sessionConflict.newUserRole}
          </span>
        </div>
        <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          Your session is no longer active on this tab. Please log in again to continue.
        </p>
        <button
          onClick={handleGoToLogin}
          style={{
            backgroundColor: '#0d6efd', color: '#fff', border: 'none',
            borderRadius: '8px', padding: '0.6rem 2rem', fontSize: '1rem',
            fontWeight: 600, cursor: 'pointer', width: '100%'
          }}
        >
          Go to Login Page
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <TrainingAuthProvider>
      <ClientAuthProvider>
      <Router>
        <SessionConflictOverlay />
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/inspection" element={<InspectionModule />} />
            <Route path="/training" element={<TrainingModule />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/training-login" element={<TrainingLogin />} />
            <Route path="/training-register" element={<TrainingRegister />} />

            {/* Client Certificate Portal — standalone, direct-URL only, not linked from the main app */}
            <Route path="/client" element={<ClientLogin />} />
            <Route path="/client/register" element={<ClientRegister />} />
            <Route
              path="/client/dashboard"
              element={
                <ClientProtectedRoute>
                  <ClientDashboard />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/client/profile"
              element={
                <ClientProtectedRoute>
                  <ClientProfile />
                </ClientProtectedRoute>
              }
            />
            <Route
              path="/client/change-password"
              element={
                <ClientProtectedRoute>
                  <ClientChangePassword />
                </ClientProtectedRoute>
              }
            />

            {/* Public Certificate Verification Routes */}
            <Route path="/verify/:token" element={<CertificateVerification />} />
            <Route path="/verify/training/:token" element={<TrainingCertVerification />} />

            {/* Protected Dashboard Routes */}
            <Route
              path="/dashboard/jr_inspector"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector']}>
                  <JuniorInspectorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/sr_inspector"
              element={
                <ProtectedRoute allowedRoles={['sr_inspector']}>
                  <SeniorInspectorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/employees-clients/:section?"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ManageEmployeesClients />
                </ProtectedRoute>
              }
            />

            {/* New Inspection Form Routes */}
            <Route
              path="/new-inspection"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector']}>
                  <NewInspectionForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspection-form/fill"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector']}>
                  <DynamicForm />
                </ProtectedRoute>
              }
            />

            {/* View Submission Details Route */}
            <Route
              path="/submissions/:id"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector', 'admin']}>
                  <ViewSubmissionDetails />
                </ProtectedRoute>
              }
            />

            {/* Edit Submission Route */}
            <Route
              path="/submission/edit/:submissionId"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector']}>
                  <EditSubmission />
                </ProtectedRoute>
              }
            />

            {/* My Drafts Route */}
            <Route
              path="/my-drafts"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector']}>
                  <MyDrafts />
                </ProtectedRoute>
              }
            />

            {/* Profile & Settings Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector', 'admin']}>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute allowedRoles={['jr_inspector', 'sr_inspector', 'admin']}>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            {/* Reports Routes */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/inspection"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <InspectionReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/training"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <TrainingReports />
                </ProtectedRoute>
              }
            />

            {/* Training Module Routes — legacy admin access */}
            <Route
              path="/training/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <TrainingDashboard />
                </ProtectedRoute>
              }
            />

            {/* Training Module Routes — training users */}
            <Route
              path="/training/coordinator"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller']}>
                  <TrainingCoordinatorDashboard />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/reviewer"
              element={
                <TrainingProtectedRoute allowedRoles={['training_reviewer', 'training_admin']}>
                  <TrainingReviewerDashboard />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/new"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller']}>
                  <NewTrainingCertificate />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/submissions/:id"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller', 'training_reviewer', 'training_admin']}>
                  <ViewTrainingSubmission />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/submissions/:id/edit"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller']}>
                  <EditTrainingSubmission />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/reports"
              element={
                <TrainingProtectedRoute allowedRoles={['training_reviewer', 'training_admin']}>
                  <TrainingReportsPage />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/profile"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller', 'training_reviewer', 'training_admin']}>
                  <TrainingEditProfile />
                </TrainingProtectedRoute>
              }
            />
            <Route
              path="/training/change-password"
              element={
                <TrainingProtectedRoute allowedRoles={['training_coordinator', 'training_document_controller', 'training_reviewer', 'training_admin']}>
                  <TrainingChangePassword />
                </TrainingProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
      </ClientAuthProvider>
      </TrainingAuthProvider>
    </AuthProvider>
  );
}

export default App;
